import { CoreTypes, readSpans, type ObjectRecord, type Primitive, type Stroke, type TextSpan, type TextStyle } from '@vp/shared-types';
import { DEFAULT_TEXT_STYLE } from '@vp/core';
import { flattenBezier } from './pathgeom';
import { isBrush, strokeWidths, taperWidths, type BrushKind } from './brush';
import { diagramPrimitives } from './diagram';
import { formOutline, formTextInset, formTextStyle, rectForm } from './forms';
import { canvasMeasure, fontString, layoutRich, textPadding } from './text';
import type { ShapeRegistry } from '@vp/dskhema';

export type ModuleRenderResult =
  | { status: 'ready'; prims: Primitive[]; stale?: boolean }
  | { status: 'pending'; prims?: Primitive[] }
  | { status: 'unavailable'; reason?: string };

/** Implemented by the module runtime: primitives for module object types (render modes A/B). */
export interface ModuleRenderProvider {
  primitives(obj: ObjectRecord, rev: number): ModuleRenderResult;
}

export interface DisplayContext {
  modules?: ModuleRenderProvider;
  /** Localized message for unavailable objects (§7.5). */
  unavailableText: string;
  /** Placeholder title of untitled frames. */
  frameLabel?: string;
  /** DSkhema: node forms contributed by modules, and the UI locale (block tags). */
  diagramShapes?: ShapeRegistry;
  outdatedDiagramLabel?: string;
  locale?: string;
  /** Format version of module object types (module forms inside diagrams). */
  formatVersion?: (type: string) => number | undefined;
  /** Manifest default props of module object types (module forms inside diagrams). */
  moduleDefaults?: (type: string) => Record<string, unknown> | undefined;
  /** API 1.5: module object types with real text — where it goes and its default style. */
  moduleText?: (type: string) => ModuleTextInfo | undefined;
  /** API 2.0: module object types that allow simplified drawing when zoomed out (manifest `lod.simplify`). */
  moduleSimplify?: (type: string) => boolean;
}

/** Text of a module object type (manifest `objectTypes[].text`). */
export interface ModuleTextInfo {
  inset: { top: number; right: number; bottom: number; left: number };
  fit: boolean;
  style: TextStyle;
}

const BOX_TYPES: ReadonlySet<string> = new Set([CoreTypes.rect, CoreTypes.ellipse, CoreTypes.triangle, CoreTypes.diamond, CoreTypes.sticker, CoreTypes.text, CoreTypes.frame, CoreTypes.image]);

/** A shape's style with its outline at most a quarter of its shorter side: a tiny shape never drowns in its stroke. */
function boxStroke(o: ObjectRecord): ObjectRecord['style'] {
  const s = o.style;
  if (!s.stroke || !BOX_TYPES.has(o.type)) return s;
  const max = Math.min(o.w, o.h) / 4;
  return max > 0 && s.stroke.width > max ? { ...s, stroke: { ...s.stroke, width: max } } : s;
}

/** Move primitives by (dx, dy). */
function shifted(prims: Primitive[], dx: number, dy: number): Primitive[] {
  if (!dx && !dy) return prims;
  return prims.map((p) => (p.k === 'path' ? { ...p, points: p.points.map((v, i) => v + (i % 2 ? dy : dx)) } : { ...p, x: p.x + dx, y: p.y + dy }));
}

/** The platform's text of a module object, drawn over the module's drawing; hidden while edited. */
function moduleTextPrimitives(obj: ObjectRecord, info: ModuleTextInfo): Primitive[] {
  const text = typeof obj.props['text'] === 'string' ? (obj.props['text'] as string) : '';
  if (!text) return [];
  const style = obj.style.text ?? info.style;
  const { top, right, bottom, left } = info.inset;
  const w = Math.max(1, obj.w - left - right);
  const h = Math.max(1, obj.h - top - bottom);
  const spans = readSpans(text, obj.props['spans']);
  const prims = shifted(spans ? richPrimitives(spans, style, w, h) : [{ k: 'text', x: 0, y: 0, w, h, text, style }], left, top);
  for (const p of prims) decorations.add(p);
  return prims;
}

/** Object → display primitives in local coords (0..w, 0..h). */
export function buildPrimitives(obj: ObjectRecord, rev: number, ctx: DisplayContext): Primitive[] {
  const { w, h } = obj;
  const style = boxStroke(obj);
  const props = obj.props;
  const text = typeof props['text'] === 'string' ? (props['text'] as string) : '';
  const textStyle: TextStyle = style.text ?? DEFAULT_TEXT_STYLE;
  const out: Primitive[] = [];
  const pushText = () => {
    if (!text) return;
    const spans = readSpans(text, props['spans']);
    if (spans) out.push(...richPrimitives(spans, textStyle, w, h));
    else out.push({ k: 'text', x: 0, y: 0, w, h, text, style: textStyle });
  };
  switch (obj.type) {
    case CoreTypes.rect: {
      const form = rectForm(obj);
      if (form) {
        const { points, closed } = formOutline(form, w, h, style.cornerRadius ?? 0, !!props['flip']);
        out.push({ k: 'path', points, closed, fill: closed ? style.fill : { kind: 'none' }, stroke: style.stroke, round: true });
        out.push(...moduleTextPrimitives({ ...obj, style: { ...style, text: formTextStyle(obj, textStyle) } }, { inset: formTextInset(obj)!, fit: false, style: textStyle }));
        return out;
      }
    }
      out.push({ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, radius: Math.min(style.cornerRadius ?? 0, w / 2, h / 2), fill: style.fill, stroke: style.stroke });
      pushText();
      return out;
    case CoreTypes.ellipse:
    case CoreTypes.triangle:
    case CoreTypes.diamond:
      out.push({
        k: 'shape',
        shape: obj.type === CoreTypes.ellipse ? 'ellipse' : obj.type === CoreTypes.triangle ? 'triangle' : 'diamond',
        x: 0,
        y: 0,
        w,
        h,
        fill: style.fill,
        stroke: style.stroke,
      });
      pushText();
      return out;
    case CoreTypes.sticker:
      out.push({ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, radius: Math.min(style.cornerRadius ?? 4, w / 2, h / 2), fill: style.fill, stroke: style.stroke });
      pushText();
      return out;
    case CoreTypes.text:
      if (style.fill.kind !== 'none' || style.stroke) {
        out.push({ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, radius: Math.min(style.cornerRadius ?? 0, w / 2, h / 2), fill: style.fill, stroke: style.stroke });
      }
      pushText();
      return out;
    case CoreTypes.frame: {
      out.push({ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, radius: 0, fill: style.fill, stroke: style.stroke });
      // the title sits above the frame, like a tab
      const title = typeof props['title'] === 'string' && props['title'] ? (props['title'] as string) : ctx.frameLabel ?? 'Frame';
      const th = frameTitleHeight(obj);
      out.push({ k: 'text', x: 0, y: -th, w: Math.max(w, 60), h: th, text: title, style: { ...textStyle, valign: 'middle', align: 'left' }, });
      return out;
    }
    case CoreTypes.image: {
      const att = props['attachment'];
      if (typeof att === 'string' && att) out.push({ k: 'image', x: 0, y: 0, w, h, src: 'att:' + att });
      else out.push(...placeholder(w, h, ''));
      if (style.stroke) out.push({ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, fill: { kind: 'none' }, stroke: style.stroke });
      return out;
    }
    case CoreTypes.line:
    case CoreTypes.connector: {
      const pts = arrowPoints(obj);
      if (pts) {
        const line = (points: number[], arrowStart: boolean, arrowEnd: boolean): Primitive => ({ k: 'path', points, closed: false, fill: { kind: 'none' }, stroke: style.stroke, arrowStart, arrowEnd });
        const lay = arrowLabelLayout(obj);
        if (lay?.cut) {
          // the line breaks around its label
          const [s0, s1] = lay.cut;
          const total = pathLength(pts);
          if (s0 > 0) out.push(line(slicePath(pts, 0, s0), !!props['arrowStart'], false));
          if (s1 < total) out.push(line(slicePath(pts, s1, total), false, !!props['arrowEnd']));
        } else out.push(line(pts, !!props['arrowStart'], !!props['arrowEnd']));
        if (lay) out.push({ k: 'text', x: lay.x, y: lay.y, w: lay.w, h: lay.h, text: lay.text, style: lay.style });
      }
      return out;
    }
    case CoreTypes.pen: {
      const pts = props['points'] as number[] | undefined;
      if (Array.isArray(pts) && pts.length >= 4) out.push(...penPrimitives(pts, style.stroke, props));
      return out;
    }
    case CoreTypes.path: {
      const closed = props['closed'] === true;
      let pts = props['points'] as number[] | undefined;
      if (!Array.isArray(pts) || pts.length < 4) pts = flattenBezier((props['nodes'] as number[] | undefined) ?? [], closed, Math.max(w, h) / 1000);
      const holes = closed ? (props['holes'] as number[][] | undefined) : undefined;
      if (pts.length >= 4) out.push({ k: 'path', points: pts, closed, ...(Array.isArray(holes) && holes.length ? { holes } : {}), fill: closed ? style.fill : { kind: 'none' }, stroke: style.stroke, round: true });
      return out;
    }
    case CoreTypes.diagram:
      return diagramPrimitives(obj, {
        shapes: ctx.diagramShapes,
        locale: ctx.locale,
        modules: ctx.modules,
        formatVersion: ctx.formatVersion,
        moduleDefaults: ctx.moduleDefaults,
        outdatedLabel: ctx.outdatedDiagramLabel,
      });
    case CoreTypes.group:
      return out;
    default: {
      if (obj.type.startsWith('core:')) return placeholder(w, h, ctx.unavailableText);
      const r = ctx.modules?.primitives(obj, rev);
      if (!r || r.status === 'unavailable') return placeholder(w, h, ctx.unavailableText);
      const textInfo = ctx.moduleText?.(obj.type);
      const own = textInfo ? moduleTextPrimitives(obj, textInfo) : [];
      const prims = r.status === 'pending' ? [...(r.prims ?? loading(w, h)), ...own] : own.length ? [...r.prims, ...own] : r.prims;
      // module text stays readable when zoomed out unless the module allows simplifying it
      if (!ctx.moduleSimplify?.(obj.type)) for (const p of prims) if (p.k === 'text') fullDetail.add(p);
      return prims;
    }
  }
}

/** Stroke of a freehand object: material and optional pressure. */
export function penPrimitives(pts: number[], stroke: Stroke | null, props: Record<string, unknown>): Primitive[] {
  if (!stroke || pts.length < 4) return [];
  const brush: BrushKind = isBrush(props['brush']) ? props['brush'] : 'ink';
  const raw = props['pressure'];
  const pressure = Array.isArray(raw) && raw.length === pts.length / 2 && raw.every((v) => typeof v === 'number' && Number.isFinite(v)) ? (raw as number[]) : undefined;
  const n = pts.length / 2;
  const explicit = isBrush(props['brush']);
  let full = strokeWidths(n, stroke.width, pressure);
  // new strokes lift off like a real nib; old strokes (no brush) keep their look
  if (explicit && brush === 'ink') full = taperWidths(pts, full, 0.35);
  const vary = explicit ? brush !== 'highlighter' || pressure !== undefined : pressure !== undefined;
  const core = (k: number, opacity: number, round: boolean, extra: Partial<Extract<Primitive, { k: 'path' }>> = {}): Primitive => ({
    k: 'path',
    points: pts,
    closed: false,
    fill: { kind: 'none' },
    stroke: { ...stroke, width: stroke.width * k },
    round,
    ...(opacity < 1 ? { opacity } : {}),
    ...(vary ? { widths: full.map((w) => w * k) } : {}),
    ...extra,
  });
  // a flat chisel tip; darkening, so text under it stays readable and overlaps do not build up
  if (brush === 'highlighter') return [core(1, 0.55, false, { blend: 'highlight' })];
  return [core(1, 1, true)];
}

/** Text drawn in full at any zoom (objects of modules without `lod.simplify`), never as bars. */
const fullDetail = new WeakSet<Primitive>();
export function keepsDetail(p: Primitive): boolean {
  return fullDetail.has(p);
}

/** Highlights and lines of formatted text: hidden together with the text while it is edited. */
const decorations = new WeakSet<Primitive>();
export function isTextDecoration(p: Primitive): boolean {
  return decorations.has(p);
}

/**
 * Formatted text → primitives: a text piece per fragment of a line (drawn on one line, left aligned,
 * at the layout's x and baseline), a highlight under it, underline and strike-through lines.
 */
export function richPrimitives(spans: readonly TextSpan[], style: TextStyle, w: number, h: number): Primitive[] {
  const lay = layoutRich(spans, style, w, h, textPadding(style));
  const marks: Primitive[] = [];
  const texts: Primitive[] = [];
  const lines: Primitive[] = [];
  for (const line of lay.lines) {
    for (const pc of line.pieces) {
      const fs = pc.style.fontSize;
      const color = pc.style.color;
      if (pc.span.bg) {
        // a marker over the whole piece, spaces included
        const full = pc.text === pc.text.trimEnd() ? pc.w : Math.max(pc.w, pc.w + fs * 0.25);
        marks.push({ k: 'shape', shape: 'rect', x: pc.x, y: line.baseline - fs * 0.95, w: full, h: fs * 1.25, radius: 0, fill: { kind: 'solid', color: pc.span.bg }, stroke: null });
      }
      const t = pc.text.trimEnd();
      if (t.trim()) {
        // one line box: with valign middle and a 1.6 × size box the baseline is 1.1 × size below its top;
        // the platform insets text by 0.45 × size, the spare width keeps the piece from wrapping
        const pad = fs * 0.45;
        texts.push({ k: 'text', x: pc.x - pad, y: line.baseline - fs * 1.1, w: pc.w * 1.2 + pad * 2 + fs, h: fs * 1.6, text: t, style: { ...pc.style, align: 'left', valign: 'middle', lineHeight: 1.25 } });
      }
      const lw = Math.max(0.6, fs * 0.07);
      if (pc.span.u && pc.w > 0) lines.push({ k: 'path', points: [pc.x, line.baseline + fs * 0.14, pc.x + pc.w, line.baseline + fs * 0.14], closed: false, fill: { kind: 'none' }, stroke: { color, width: lw, dash: 'solid' } });
      if (pc.span.s && pc.w > 0) lines.push({ k: 'path', points: [pc.x, line.baseline - fs * 0.3, pc.x + pc.w, line.baseline - fs * 0.3], closed: false, fill: { kind: 'none' }, stroke: { color, width: lw, dash: 'solid' } });
    }
  }
  for (const p of marks) decorations.add(p);
  for (const p of lines) decorations.add(p);
  return [...marks, ...texts, ...lines];
}

export function placeholder(w: number, h: number, message: string): Primitive[] {
  const out: Primitive[] = [
    {
      k: 'shape',
      shape: 'rect',
      x: 0,
      y: 0,
      w,
      h,
      fill: { kind: 'lines', color: '#d1d5db', background: '#f9fafb', spacing: 12, angle: Math.PI / 4 },
      stroke: { color: '#9ca3af', width: 1, dash: 'dashed' },
    },
  ];
  if (message) {
    out.push({
      k: 'text',
      x: 0,
      y: 0,
      w,
      h,
      text: message,
      style: { ...DEFAULT_TEXT_STYLE, fontSize: Math.max(6, Math.min(14, Math.min(w, h) / 6)), color: '#4b5563' },
    });
  }
  return out;
}

function loading(w: number, h: number): Primitive[] {
  return [{ k: 'shape', shape: 'rect', x: 0, y: 0, w, h, radius: 4, fill: { kind: 'solid', color: '#f3f4f6' }, stroke: { color: '#e5e7eb', width: 1, dash: 'dashed' } }];
}

/** Points of an arrow as drawn: a connector's bends are slightly rounded. Null without a path. */
export function arrowPoints(obj: ObjectRecord): number[] | null {
  const pts = (obj.type === CoreTypes.line ? obj.props['points'] : obj.props['path']) as number[] | undefined;
  if (!Array.isArray(pts) || pts.length < 4) return null;
  if (obj.type !== CoreTypes.connector || obj.props['route'] === 'curved' || pts.length < 6) return pts;
  return roundedBends(pts, Math.max(6, (obj.style.stroke?.width ?? 2) * 4));
}

/** An open polyline with its inner corners rounded by `r` (kept within half of each stretch). */
export function roundedBends(pts: number[], r: number): number[] {
  const n = pts.length / 2;
  const out = [pts[0], pts[1]];
  for (let i = 1; i < n - 1; i++) {
    const [px, py, x, y, nx, ny] = [pts[i * 2 - 2], pts[i * 2 - 1], pts[i * 2], pts[i * 2 + 1], pts[i * 2 + 2], pts[i * 2 + 3]];
    const l1 = Math.hypot(x - px, y - py);
    const l2 = Math.hypot(nx - x, ny - y);
    const k = Math.min(r, l1 / 2, l2 / 2);
    if (k <= 0.01) {
      out.push(x, y);
      continue;
    }
    const ax = x + ((px - x) / l1) * k;
    const ay = y + ((py - y) / l1) * k;
    const bx = x + ((nx - x) / l2) * k;
    const by = y + ((ny - y) / l2) * k;
    out.push(ax, ay);
    for (let s = 1; s <= 6; s++) {
      const t = s / 6;
      const u = 1 - t;
      out.push(u * u * ax + 2 * u * t * x + t * t * bx, u * u * ay + 2 * u * t * y + t * t * by);
    }
  }
  out.push(pts[pts.length - 2], pts[pts.length - 1]);
  return out;
}

/**
 * Where an arrow's label goes (local coords): centred on the middle of the arrow (or at `labelAt`),
 * no plate — the line breaks around the text (`cut`: the stretch of path length left out).
 * Null without a label. `text` overrides the label (the editor sizes itself on what is being typed).
 */
export function arrowLabelLayout(obj: ObjectRecord, text?: string): { x: number; y: number; w: number; h: number; text: string; style: TextStyle; cut: [number, number] | null } | null {
  const pts = arrowPoints(obj);
  const label = text ?? (typeof obj.props['label'] === 'string' ? (obj.props['label'] as string) : '');
  if (!label || !pts) return null;
  const at = typeof obj.props['labelAt'] === 'number' ? Math.min(1, Math.max(0, obj.props['labelAt'] as number)) : 0.5;
  const total = pathLength(pts);
  const base = obj.style.text ?? DEFAULT_TEXT_STYLE;
  const fs = fitArrowLabel(label, base, total);
  const style: TextStyle = { ...base, fontSize: fs, align: 'center', valign: 'middle' };
  const th = fs * (base.lineHeight ?? 1.25);
  const tw = Math.max(fs * 0.6, canvasMeasure(label, fontString(style, fs))) + fs * 0.9;
  const mid = pathMidpoint(pts, at);
  const box = { x: mid.x - tw / 2, y: mid.y - th / 2, w: tw, h: th };
  // the break: from the middle along the path both ways until the line leaves the text's box
  const pad = fs * 0.15;
  const inside = (p: { x: number; y: number }) => p.x >= box.x - pad && p.x <= box.x + box.w + pad && p.y >= box.y - pad && p.y <= box.y + box.h + pad;
  const step = Math.max(0.5, fs * 0.05);
  const s = total * at;
  let s0 = s;
  while (s0 > 0 && inside(pathMidpoint(pts, s0 / total))) s0 -= step;
  let s1 = s;
  while (s1 < total && inside(pathMidpoint(pts, s1 / total))) s1 += step;
  return { ...box, text: label, style, cut: [Math.max(0, s0), Math.min(total, s1)] };
}

/** Part of a polyline between path lengths s0 and s1. */
export function slicePath(pts: number[], s0: number, s1: number): number[] {
  const out: number[] = [];
  let acc = 0;
  const at = (i: number, t: number) => [pts[i - 2] + (pts[i] - pts[i - 2]) * t, pts[i - 1] + (pts[i + 1] - pts[i - 1]) * t];
  for (let i = 2; i < pts.length; i += 2) {
    const seg = Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
    const a = acc;
    const b = acc + seg;
    acc = b;
    if (b < s0 || a > s1 || seg === 0) continue;
    if (!out.length) out.push(...at(i, Math.max(0, (s0 - a) / seg)));
    out.push(...at(i, Math.min(1, (s1 - a) / seg)));
  }
  return out.length >= 4 ? out : [pts[0], pts[1], pts[0], pts[1]];
}

/** Length of a polyline in the same units as its points. */
export function pathLength(pts: number[]): number {
  let total = 0;
  for (let i = 2; i < pts.length; i += 2) total += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  return total;
}

/**
 * Font size so a one-line label sits on the arrow: at most `style.fontSize`, shrunk until the plate
 * is shorter than the path (leaves a little at each end). Never below 4.
 */
export function fitArrowLabel(text: string, style: TextStyle, pathLen: number): number {
  const wanted = style.fontSize;
  if (!text || pathLen <= 0) return wanted;
  const room = Math.max(8, pathLen * 0.92);
  const extra = 1;
  const atWanted = canvasMeasure(text, fontString(style, wanted)) + wanted * extra;
  if (atWanted <= room) return wanted;
  const perEm = canvasMeasure(text, fontString(style, 1)) + extra;
  if (perEm <= 0) return wanted;
  return Math.max(4, Math.min(wanted, room / perEm));
}

/** Point at fraction `f` of a polyline's length (the middle by default). */
export function pathMidpoint(pts: number[], f = 0.5): { x: number; y: number } {
  let total = 0;
  for (let i = 2; i < pts.length; i += 2) total += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  let acc = 0;
  for (let i = 2; i < pts.length; i += 2) {
    const seg = Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
    if (acc + seg >= total * f && seg > 0) {
      const t = (total * f - acc) / seg;
      return { x: pts[i - 2] + (pts[i] - pts[i - 2]) * t, y: pts[i - 1] + (pts[i + 1] - pts[i - 1]) * t };
    }
    acc += seg;
  }
  return { x: pts[0], y: pts[1] };
}

/** Height of the title strip drawn above a frame (world units). */
export function frameTitleHeight(o: ObjectRecord): number {
  return (o.style.text?.fontSize ?? 14) * 1.9;
}
